import React from 'react';
import { format } from 'date-fns';
import WeekView from './WeekView';
import DayGlance from './day/DayGlance';
import DayEmpty from './day/DayEmpty';
import { reading } from '../../domain/reading';
import { useStudents } from '../../data/useStudents';
import { dayGlance } from '../../domain/teaching';
import { useLocale } from '../../context/LocaleContext';
import s from './day/dayView.module.css';

// The Day view: the day's schedule against the clock, with its summary beside.
//
// ── The time grid, one column wide (#572) ──────────────────────────────────
//
// Week · Agenda is the time grid (`WEEK_MODES.agenda` in
// hooks/useCalendarView.js, drawn by WeekView), and this is that grid one
// column wide: the same hours, cards and all-day band, and the same three
// drags — a slot to add, a bottom edge to lengthen, a lesson to move (#571).
// MMS's Week and Day share the time grid and the all-day band too (#160).
//
// The glance card beside the grid — lessons, teaching hours, attended, billed
// today — is the one place the calendar adds up a day.
//
// ── The card's four numbers are the core's (#470) ──────────────────────────
//
// `teaching::day_glance` in core/src/teaching.rs, through `dayGlance` in
// domain/teaching.js, the fold both phones call. It decides:
//
//   * Teaching hours counts an all-day event (#160, #313). Hours are not
//     money, and excluding all-day from the hours would make this tile
//     disagree with the money tile for three pricing modes out of four.
//   * Only the rows that bill are priced (#159), and "does it bill" is
//     `is_billable_for` with the occurrence's make-up flag (#362) — not
//     "exclude anything teacher cancelled": `teacher_cancelled_issue_credit`
//     bills, and `absent_no_credit`, which sounds unbilled, bills too.
//   * A lesson's stamped price, else the student's mode, through
//     `price_occurrence` with the all-day flag (#322), and nothing today for a
//     mode that cannot price one lesson: a fixed-monthly fee is not today's
//     money.
//
// `lessonCount`, `teachingHours` and `attendedCount` count everything on the
// day, a cancelled lesson included: it is still on the calendar, and a
// teacher looking at their day should see the hole.
//
// ── "+ Add a lesson" is Quick-Add (#573) ───────────────────────────────────
//
// Both add buttons — the free-day card and the line under the summary — open
// Quick-Add Lesson on this date (CalendarPage.jsx), rather than the full form,
// whose attendee picker is a multi-select. A slot on the grid opens the full
// form, as on the week and in MMS.
//
// ── Not before the schedule has been read (#626) ──────────────────────────
//
// `loading` is `useEvents`' — the series listener has not answered. Until it
// has, the sidebar says nothing: "A free Tuesday." over an unread schedule is
// a claim this view cannot make. `CalendarPage`'s empty month waits for the
// same flag.
//
// ── Two shapes of the same day (#749) ──────────────────────────────────────
//
// `blocks` is what the grid draws, one per occurrence, so a group lesson is
// one block (`2 students`) beside whatever overlaps it. `lessons` is the same
// day one row per student, which the summary adds up: `dayGlance` counts and
// prices a group per student. Both are `useCalendarOccurrences`'.
export default function DayView({
  date, lessons, blocks, categoryColorFor, onLessonClick, onAddLesson,
  onSlotClick, onResizeLesson, onMoveLesson, loading = false, failed = false,
}) {
  const { getStudent } = useStudents();
  const { formatWeekdayLong } = useLocale();
  const glance = dayGlance(lessons, getStudent);

  return (
    <div className={s.dayView}>
      <div className={s.schedule}>
        <WeekView
          days={[date]}
          blocks={blocks}
          categoryColorFor={categoryColorFor}
          onLessonClick={onLessonClick}
          onSlotClick={onSlotClick}
          onResizeLesson={onResizeLesson}
          onMoveLesson={onMoveLesson}
        />
      </div>

      {/* Every lesson comes from a series, so none is on the day while the
          series are unread: the sidebar is empty rather than "0 lessons". */}
      {lessons.length === 0 && loading && !failed ? (
        <div className={s.sidebar} />
      ) : (
        <div className={s.sidebar}>
          {lessons.length === 0 ? (
            <DayEmpty
              reading={reading(!loading, lessons.length, failed)}
              weekdayName={formatWeekdayLong(format(date, 'yyyy-MM-dd'))}
              onAddLesson={onAddLesson}
            />
          ) : (
            <button className={s.addCta} onClick={onAddLesson}>
              Nothing else today. <span className={s.addCtaAction}>+ Add a lesson</span>
            </button>
          )}
          <DayGlance
            lessonCount={glance.lessonCount}
            teachingHoursText={glance.teachingHoursText}
            attendedCount={glance.attendedCount}
            billedMinor={glance.billedMinor}
          />
        </div>
      )}
    </div>
  );
}
